import { BrowserSupport } from '@tableau/browser-support';
import { Delegate } from './utility/Delegate';
import { DomSubscriptionHandle, HTMLEventHandler } from './DomSubscriptionHandle';
import { DomUtil } from './utility/DomUtil';
import { EventHandleSpec } from './EventHandleSpec';
import { EventRecognizer } from './EventRecognizer';
import { FeatureFlag, FeatureParam, TsConfig } from './utility/VizclientUtils';
import { IDisposable } from './definition/IDisposable';
import { MiscUtil } from './utility/MiscUtil';
import { NormalizedEvent } from './event/NormalizedEvent';
import { PointerEventDetector } from './PointerEventDetector';
import { PseudoEventName } from '../gen-src/preslayer/Enums';
import { TypeUtil } from './utility/TypeUtil';

export type TableauEventHandlerDelegate = (_: TableauEventHandler) => void;
export const OptOutAttributeName = 'data-eventutils-optout';

/**
  Interface to support the IE11 Element.closest fill in TableauEventHandler.ts
  This and the function below can be deleted when IE11 support has been dropped.
  */
declare global {
  interface Element {
    msMatchesSelector(selectors: string): boolean;
  }
}

// tslint:disable:strict-boolean-expressions
/** Replacement for Element.closest -- can switch to Element.closest once we drop IE11 support */
function closest(element: Element, selector: string): Element | null {
  if (element.closest) { return element.closest(selector); }

  const matches = Element.prototype.matches || Element.prototype.msMatchesSelector;
  if (!matches) { return null; }

  let node = element;
  while (node) {
    if (matches.call(node, selector)) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}
// tslint:enable:strict-boolean-expressions

/**
* This class listens to all the mouse and touch events on a specific DOM element.
* It processes(with help of other classes) these events and manage invoking callbacks
* specified in EventHandlerSpec object it is initialized with.
*/
export class TableauEventHandler implements IDisposable {
  protected config: EventHandleSpec;
  private eventRecognizer: EventRecognizer;
  private subscriptionHandle: DomSubscriptionHandle = null;

  /**
  * Initializes a new instance of the TableauEventHandler class
  * Takes domElement and an object with callbacks - see documentation in EventHandlerSpec.cs
  * @param {HTMLElement} domElement - Element object
  * @param {EventHandleSpec} config - GestureEventHandleSpec object
  */
  public constructor(domElement: HTMLElement = null, config: EventHandleSpec = null) {
    if (MiscUtil.IsNullOrUndefined(domElement) || MiscUtil.IsNullOrUndefined(EventHandleSpec)) {
      return;
    }
    // tslint:disable-next-line:strict-boolean-expressions
    this.config = config || new EventHandleSpec();
    this.config.DomElement = domElement;

    if (FeatureFlag.IsEventHandlingReworkEnabled() && !PointerEventDetector.initialized) {
      PointerEventDetector.init();
    }

    this.ConfigureDomElem(domElement);
    this.SetupEventRecognizer(this.config);
    this.MakeConnections(domElement);

    // #if DEBUG
    TableauEventHandler.RaiseTableauEventHandlerCreated(this);
    // #endif
  }

  public get DomElement(): Element {
    return this.config.DomElement;
  }

  protected SetupEventRecognizer(spec: EventHandleSpec): void {
    this.eventRecognizer = new EventRecognizer(spec);
    // If we have a press Drag, set up the callback modifications
    this.eventRecognizer.SetupPressDrag(spec);
  }

  public suppressHover(): void {
    if (this.config.IsUsingHover()) {
      this.eventRecognizer.suppressHover();
    }
  }

  public unsuppressHover(): void {
    if (this.config.IsUsingHover()) {
      this.eventRecognizer.unsuppressHover();
    }
  }

  public CancelHover(): void {
    if (this.config.IsUsingHover()) {
      this.eventRecognizer.CancelHover();
    }
  }

  public GetHandledEvents(): Array<PseudoEventName> {
    let handledEvents: Array<PseudoEventName> = new Array<PseudoEventName>();

    let configMembers = this.config as Object;

    for (const key of Object.keys(configMembers)) {
      let curVal = TypeUtil.GetField(configMembers, key);
      // TODO: - the original check is more stringent -- should we copy that?
      // configMembers[key].GetType() == typeof(Action<NormalizedEvent>))
      if (!MiscUtil.IsNullOrUndefined(curVal) && (typeof curVal === 'function')) {
        handledEvents.push(key as PseudoEventName);
      }
    }
    return handledEvents;
  }

  public Update(spec: EventHandleSpec): void {
    $.extend(this.config, spec);
    this.eventRecognizer.UpdateAllGestures(this.config);
    this.eventRecognizer.SetupPressDrag(this.config);
  }

  /**
   * Same as dispose. This only exists to make old javascript code happy :)
   */
  public destroy(): void {
    this.dispose();
  }

  // kill the listeners
  public dispose(): void {
    // #if DEBUG
    TableauEventHandler.RaiseTableauEventHandlerToBeDisposed(this);
    // #endif
    if (!MiscUtil.IsNullOrUndefined(this.subscriptionHandle)
      && !MiscUtil.IsNullOrUndefined(this.subscriptionHandle.DomElement)
      && !MiscUtil.IsNullOrUndefined(this.subscriptionHandle.EventHandler)) {
      for (let handledEvent of NormalizedEvent.AllHandledEvents) {
        this.subscriptionHandle.DomElement.removeEventListener(handledEvent.toString(), this.subscriptionHandle.EventHandler);
      }
    }

    this.eventRecognizer.dispose();
  }

  public static GetHandler(spec: EventHandleSpec, evt: PseudoEventName): (_: NormalizedEvent) => void {
    return TypeUtil.GetField(spec, evt);
  }

  protected ConfigureDomElem(domElem: HTMLElement): void {
    // Add css for mobile to suppress various mobile feedback.  This originated with CL 119825.

    // TFS583075
    // If the dom element is an input, or defines user-select or -webkit-user-select then don't set it to none.
    // When setting it to none on text inputs it causes users to be unable to enter text on browsers using webkit.
    let userStyle = TypeUtil.GetField(domElem.style, 'user-select');
    let webkitUserStyle = TypeUtil.GetField(domElem.style, '-webkit-user-select');
    if (domElem.tagName.toLowerCase() !== 'input' &&
      (MiscUtil.IsNullOrUndefined(userStyle) || userStyle === '') &&
      (MiscUtil.IsNullOrUndefined(webkitUserStyle) || webkitUserStyle === '')) {
      TypeUtil.SetField(domElem.style, '-webkit-user-select', 'none');
    }
    TypeUtil.SetField(domElem.style, '-webkit-touch-callout', 'none');
    TypeUtil.SetField(domElem.style, '-webkit-tap-highlight-color', 'transparent');


    // Add css to suppress default browser pan actions.  BUGZID 60153
    // NOTE-mroginski-2014-03-21: touch-action currently only works for IE10 and IE11, no way to suppress default
    // browser action on chrome yet.
    // NOTE-jrockwood-2014-12-09: BUGZID: 138891 - If we use touch-action: none it will disable scrolling and
    // panning and zooming. This is what we wanted since it can interfere with multi-select and can sometimes
    // behave in a quirky way. However, customers would rather have the pan/zoom and scrolling. We really need to
    // rework the way we handle touch in the browser, but that's a big work item and we can't do it now. Note that
    // IE behaves differently than the other browsers (are you surprised :) and we actually do need to have
    // touch-action set to none, but only for IE. As such, we're using the MS-specific CSS attribute. It's supported
    // in IE 10 and still works in IE 11, but it has been deprecated and may be removed in IE 12.
    TypeUtil.SetField(domElem.style, '-ms-touch-action', 'none');
    // domElem.Style["touch-action"] = "none";                        // disable browser default actions in ie11 and forward
  }

  private MultiTouchWithDifferentTargetElement(e: NormalizedEvent): boolean {
    if (e.touches.length > 1 && !e.AllTouchesShareCurrentTarget()) {
      this.eventRecognizer.CancelAllGestures();
      return true;
    }
    return false;
  }

  // single entry point for all events. Preserve method name for testing.
  // [PreserveName]
  protected handler(e: NormalizedEvent): void {
    // tslint:disable-next-line
    const optOut: boolean = e.target && !!closest(e.target, `[${OptOutAttributeName}="true"]`);
    if (optOut) {
      return;
    }


    // Unhandled touch events generate emulated mouse events. For example, touchstart generates mousedown, touchend generates mouseup,
    // and touchmove generates mousemove. See also https://developer.mozilla.org/en-US/docs/Web/API/Touch_events/Using_Touch_Events
    // In web, to support hybrid devices, we handle touch and mouse events, ignoring emulated mouse events using PointerEventDetector or
    // preventing the browser from firing emulated mouse events using preventDefault.
    // In mobile we don't listen to mouse events.
    const isEmulatedMouseEvent = e.IsMouseEvent
      && BrowserSupport.IsTouchEventSupported() // If touch events aren't supported then there can't be emulated mouse events
      && PointerEventDetector.currentPointerType === 'touch';

    if (!TsConfig.IsMobile()) {
      if (FeatureFlag.IsEventHandlingReworkEnabled() && isEmulatedMouseEvent) {
        // If we get here then we are handling a emulated mouse event that was triggered by the browser after a user touched
        // an element on a non-mobile device. We should have already handled the touch events so we return early to ignore
        // the emulated mouse event.
        return;
      } else if (!FeatureFlag.IsEventHandlingReworkEnabled() && e.IsTouchEvent) {
        if (DomUtil.CanCallPreventDefaultOnTouchEvent(e.target)) {
          // If get here then we are handling a touch event on a non-mobile device so we call preventDefault (to prevent mouse events)
          // then fall into the code below, which will handle the touch event.
          e.preventDefault();
        } else {
          return;
        }
      }
    }

    if (this.MultiTouchWithDifferentTargetElement(e)) {
      return;
    }
    this.eventRecognizer.HandleEvent(e);
  }

  private MakeConnections(domElem: HTMLElement): void {
    let handler: HTMLEventHandler = (e: Event) => {
      let normalizedEvent: NormalizedEvent = NormalizedEvent.Create(e as TouchEvent, this.config.DomElement);
      this.handler(normalizedEvent);
    };

    this.subscriptionHandle = new DomSubscriptionHandle(domElem, handler);
    for (let handledEvent of NormalizedEvent.AllHandledEvents) {
      // IE11 currently does not support passive listeners: https://caniuse.com/#search=Passive%20event%20listeners
      if (BrowserSupport.IsIE()) {
        domElem.addEventListener(handledEvent.toString(), handler, false);
      } else {
        domElem.addEventListener(handledEvent.toString(), handler, {
          capture: false,
          passive: false
        });
      }
    }
  }

  public static TableauEventHandlerCreated: Delegate<TableauEventHandlerDelegate> = new Delegate<TableauEventHandlerDelegate>();
  public static add_TableauEventHandlerCreated(ev: TableauEventHandlerDelegate): void {
    TableauEventHandler.TableauEventHandlerCreated.add(ev);
  }
  public static remove_TableauEventHandlerCreated(ev: TableauEventHandlerDelegate): void {
    TableauEventHandler.TableauEventHandlerCreated.remove(ev);
  }

  public static TableauEventHandlerToBeDisposed: Delegate<TableauEventHandlerDelegate> = new Delegate<TableauEventHandlerDelegate>();
  public static add_TableauEventHandlerToBeDisposed(ev: TableauEventHandlerDelegate): void {
    TableauEventHandler.TableauEventHandlerToBeDisposed.add(ev);
  }
  public static remove_TableauEventHandlerToBeDisposed(ev: TableauEventHandlerDelegate): void {
    TableauEventHandler.TableauEventHandlerToBeDisposed.remove(ev);
  }

  protected static RaiseTableauEventHandlerCreated(handler: TableauEventHandler): void {
    if (FeatureParam.DebugTools() && TableauEventHandler.TableauEventHandlerCreated !== null) {
      TableauEventHandler.TableauEventHandlerCreated.invoke(handler);
    }
  }

  protected static RaiseTableauEventHandlerToBeDisposed(handler: TableauEventHandler): void {
    if (FeatureParam.DebugTools() && TableauEventHandler.TableauEventHandlerToBeDisposed !== null) {
      TableauEventHandler.TableauEventHandlerToBeDisposed.invoke(handler);
    }
  }
}

